import { Contact, Maybe, Plan } from '../../generated';
import { AppContextReducerState } from '../interfaceTypes';

export const initialState: AppContextReducerState = {
  isDelete: false,
  isOpen: false,
  isEdit: false,
  isEditContact: false,
  timeLeft: 60,
  cardsData: [],
  optOutContact: [],
  currentCardData: null,
  contactIndex: 0,
  isDragging: false,
  contactCount: 1,
  selectedPlan: null,
  isNameEdit: false,
  isEmailEdit: false,
  isEmailChanged: false,
  isPhoneEdit: false,
  isSecurityPinEdit: false,
  isPasswordChanged: false,
  isBillingAddress: false,
  totalDataCount: 0,
  currentPage: 1,
  totalPageCount: 2,
  dragAndDrop: false,
  isChangePriority: false,
  isChangePlan: false,
  isAppUserLoggedIn: false,
  currentUser: null,
  appUserLoader: false
};

export enum ActionType {
  SET_IS_OPEN = 'setIsOpen',
  SET_IS_DELETE = 'setIsDelete',
  SET_IS_EDIT = 'setIsEdit',
  SET_IS_EDIT_CONTACT = 'setIsEditContact',
  SET_TIME_LEFT = 'setTimeLeft',
  SET_CARDS_DATA = 'setCardsData',
  SET_CURRENT_CARD = 'setCurrentCard',
  SET_CONTACT_INDEX = 'setContactIndex',
  SET_IS_DRAGGING = 'setIsDragging',
  SET_CONTACT_COUNT = 'setIsContactCount',
  SET_SELECTED_PLAN = 'setSelectedPlan',
  SET_IS_NAME_EDIT = 'setIsNameEdit',
  SET_IS_EMAIL_EDIT = 'setIsEmailEdit',
  SET_IS_PHONE_EDIT = 'setIsPhoneEdit',
  SET_IS_SECURITY_PIN_EDIT = 'isSecurityPinEdit',
  SET_IS_EMAIL_CHANGED = 'isEmailChanged',
  SET_IS_PASSWORD_CHANGED = 'isPasswordChanged',
  SET_IS_BILLING_ADDRESS = 'isBillingAddress',
  SET_TOTAL_DATA_COUNT = 'setTotalDataCount',
  SET_CURRENT_PAGE = 'setCurrentPage',
  SET_TOTAL_PAGE_COUNT = 'setTotalPageCount',
  SET_DRAG_AND_DROP = 'setDragAndDrop',
  SET_IS_CHANGE_PRIORITY = 'setIsChangePriority',
  SET_IS_CHANGE_PLAN = 'setIsChangePlan',
  SET_CONTACTS_OPT_OUT = 'setContactsOptOut'
}

export type AppAction =
  | { type: ActionType.SET_IS_OPEN; isOpen: boolean }
  | { type: ActionType.SET_IS_DELETE; isDelete: boolean }
  | { type: ActionType.SET_TIME_LEFT; timeLeft: number }
  | { type: ActionType.SET_IS_EDIT; isEdit: boolean }
  | { type: ActionType.SET_IS_EDIT_CONTACT; isEditContact: boolean }
  | { type: ActionType.SET_CARDS_DATA; cardsData: Array<Contact> }
  | { type: ActionType.SET_CONTACTS_OPT_OUT; optOutContact: Array<Contact> }
  | { type: ActionType.SET_CURRENT_CARD; currentCardData: Maybe<Contact> | undefined }
  | { type: ActionType.SET_CONTACT_INDEX; contactIndex: number }
  | { type: ActionType.SET_IS_DRAGGING; isDragging: boolean }
  | { type: ActionType.SET_CONTACT_COUNT; contactCount: number }
  | { type: ActionType.SET_SELECTED_PLAN; selectedPlan: Plan }
  | { type: ActionType.SET_IS_NAME_EDIT; isNameEdit: boolean }
  | { type: ActionType.SET_IS_EMAIL_EDIT; isEmailEdit: boolean }
  | { type: ActionType.SET_IS_EMAIL_CHANGED; isEmailChanged: boolean }
  | { type: ActionType.SET_IS_PHONE_EDIT; isPhoneEdit: boolean }
  | { type: ActionType.SET_IS_SECURITY_PIN_EDIT; isSecurityPinEdit: boolean }
  | { type: ActionType.SET_IS_PASSWORD_CHANGED; isPasswordChanged: boolean }
  | { type: ActionType.SET_TOTAL_DATA_COUNT; totalDataCount: number }
  | { type: ActionType.SET_CURRENT_PAGE; currentPage: number }
  | { type: ActionType.SET_TOTAL_PAGE_COUNT; totalPageCount: number }
  | { type: ActionType.SET_IS_BILLING_ADDRESS; isBillingAddress: boolean }
  | { type: ActionType.SET_DRAG_AND_DROP; dragAndDrop: boolean }
  | { type: ActionType.SET_IS_CHANGE_PRIORITY; isChangePriority: boolean }
  | { type: ActionType.SET_IS_CHANGE_PLAN; isChangePlan: boolean };

export const reducer = (state: AppContextReducerState, action: AppAction): AppContextReducerState => {
  switch (action.type) {
    case ActionType.SET_IS_OPEN:
      return {
        ...state,
        isOpen: action.isOpen
      };

    case ActionType.SET_IS_DELETE:
      return {
        ...state,
        isDelete: action.isDelete
      };

    case ActionType.SET_TIME_LEFT:
      return {
        ...state,
        timeLeft: action.timeLeft
      };

    case ActionType.SET_IS_EDIT:
      return {
        ...state,
        isEdit: action.isEdit
      };

    case ActionType.SET_IS_EDIT_CONTACT:
      return {
        ...state,
        isEditContact: action.isEditContact
      };

    case ActionType.SET_CARDS_DATA:
      return {
        ...state,
        cardsData: action.cardsData
      };

    case ActionType.SET_CURRENT_CARD:
      return {
        ...state,
        currentCardData: action.currentCardData
      };

    case ActionType.SET_CONTACT_INDEX:
      return {
        ...state,
        contactIndex: action.contactIndex
      };

    case ActionType.SET_IS_DRAGGING:
      return {
        ...state,
        isDragging: action.isDragging
      };

    case ActionType.SET_CONTACT_COUNT:
      return {
        ...state,
        contactCount: action.contactCount
      };

    case ActionType.SET_SELECTED_PLAN:
      return {
        ...state,
        selectedPlan: action.selectedPlan
      };

    case ActionType.SET_IS_NAME_EDIT:
      return {
        ...state,
        isNameEdit: action.isNameEdit
      };

    case ActionType.SET_IS_EMAIL_EDIT:
      return {
        ...state,
        isEmailEdit: action.isEmailEdit
      };

    case ActionType.SET_IS_EMAIL_CHANGED:
      return {
        ...state,
        isEmailChanged: action.isEmailChanged
      };

    case ActionType.SET_IS_PHONE_EDIT:
      return {
        ...state,
        isPhoneEdit: action.isPhoneEdit
      };

    case ActionType.SET_IS_SECURITY_PIN_EDIT:
      return {
        ...state,
        isSecurityPinEdit: action.isSecurityPinEdit
      };

    case ActionType.SET_IS_PASSWORD_CHANGED:
      return {
        ...state,
        isPasswordChanged: action.isPasswordChanged
      };

    case ActionType.SET_IS_BILLING_ADDRESS:
      return {
        ...state,
        isBillingAddress: action.isBillingAddress
      };

    case ActionType.SET_TOTAL_DATA_COUNT:
      return {
        ...state,
        totalDataCount: action.totalDataCount
      };

    case ActionType.SET_CURRENT_PAGE:
      return {
        ...state,
        currentPage: action.currentPage
      };

    case ActionType.SET_TOTAL_PAGE_COUNT:
      return {
        ...state,
        totalPageCount: action.totalPageCount
      };

    case ActionType.SET_DRAG_AND_DROP:
      return {
        ...state,
        dragAndDrop: action.dragAndDrop
      };

    case ActionType.SET_IS_CHANGE_PRIORITY:
      return {
        ...state,
        isChangePriority: action.isChangePriority
      };

    case ActionType.SET_CONTACTS_OPT_OUT:
      return {
        ...state,
        optOutContact: action.optOutContact
      };

    case ActionType.SET_IS_CHANGE_PLAN:
      return {
        ...state,
        isChangePlan: action.isChangePlan
      };
  }
};
